import { Suspense, lazy, useId, useState, type ReactNode } from 'react';
import type { Channel } from '@neuroedge/shared';
import { Button, Card, PageState } from '../../components/ui';
import {
  TIME_BASED_TRIGGERS,
  blankAction,
  definitionFromForm,
  formFromDefinition,
  messageTriggers,
  type ActionDraft,
  type ActionKind,
  type AutomationFormState,
} from './automation-model';
import { FieldError, muted } from './automation-form-parts';
import {
  ActionCard,
  AddActionPicker,
  IfEditor,
  WhenEditor,
  type ActionCardLookups,
} from './automation-sections';

const AutomationBuilder = lazy(() => import('./builder/AutomationBuilder'));

type View = 'visual' | 'form' | 'json';

function sectionTitle(n: number, title: string, hint?: string) {
  return (
    <div>
      <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>
        <span style={{ color: 'var(--color-primary)' }}>{n}.</span> {title}
      </h2>
      {hint ? <p style={{ margin: 'var(--space-1) 0 0', ...muted }}>{hint}</p> : null}
    </div>
  );
}

export interface AutomationDefinitionEditorProps {
  form: AutomationFormState;
  setForm: (update: (f: AutomationFormState) => AutomationFormState) => void;
  errors: Record<string, string>;
  setErrors: (update: (e: Record<string, string>) => Record<string, string>) => void;
  channels: Channel[];
  replyOptions: { id: string; title: string; from: string }[];
  lookups: ActionCardLookups;
  newAction: (kind: ActionKind) => ActionDraft;
  flowPreview?: (flowKey: string) => ReactNode;
  /** Reports whether the JSON view holds something unparseable (saving should be blocked). */
  onJsonValidityChange?: (valid: boolean) => void;
}

/**
 * The When / If / Then of an automation, in three views over the same state:
 * the visual builder (default, like conversation flows), the step-by-step
 * form, and "Advanced: edit as JSON". Shared by tenant automations and the
 * platform template library.
 */
export function AutomationDefinitionEditor({
  form,
  setForm,
  errors,
  setErrors,
  channels,
  replyOptions,
  lookups,
  newAction,
  flowPreview,
  onJsonValidityChange,
}: AutomationDefinitionEditorProps) {
  const jsonId = useId();
  const [view, setView] = useState<View>('visual');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [addingAction, setAddingAction] = useState(false);

  const set = (patch: Partial<AutomationFormState>) => setForm((f) => ({ ...f, ...patch }));
  const clear = (...keys: string[]) =>
    setErrors((e) => {
      const next = { ...e };
      for (const k of keys) delete next[k];
      return next;
    });
  const hasMessageTrigger = messageTriggers(form.triggers);
  const timeBased = form.triggers.some((t) => TIME_BASED_TRIGGERS.has(t));

  function switchTo(next: View) {
    if (next === view) return;
    if (view === 'json' && jsonError) return; // stay until the JSON parses
    if (next === 'json') {
      setJsonText(JSON.stringify(definitionFromForm(form), null, 2));
      setJsonError(null);
    }
    setView(next);
  }

  function editJson(text: string) {
    setJsonText(text);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setJsonError('This is not valid JSON yet.');
      onJsonValidityChange?.(false);
      return;
    }
    const next = formFromDefinition(form, parsed);
    if (!next) {
      setJsonError('It needs "triggers" and "actions" lists.');
      onJsonValidityChange?.(false);
      return;
    }
    // Applied as you type, so saving from the JSON view saves exactly this.
    setForm(() => next);
    setJsonError(null);
    onJsonValidityChange?.(true);
  }

  const tabs: { id: View; label: string }[] = [
    { id: 'visual', label: 'Visual' },
    { id: 'form', label: 'Form' },
  ];

  return (
    <div
      style={{ display: 'grid', gap: 'var(--space-3)' }}
      // Enter in a one-line field must not submit (save) the whole automation mid-edit.
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') e.preventDefault();
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
      >
        <div role="tablist" aria-label="Automation view" className="ne-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={view === t.id}
              className="ne-tabs__tab"
              onClick={() => switchTo(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        {view === 'json' ? (
          <Button type="button" variant="secondary" onClick={() => switchTo('visual')}>
            Back to visual builder
          </Button>
        ) : (
          <Button type="button" variant="secondary" onClick={() => switchTo('json')}>
            Advanced: edit as JSON
          </Button>
        )}
      </div>

      {view === 'visual' ? (
        <Suspense fallback={<PageState kind="loading" message="Loading the visual builder…" />}>
          <AutomationBuilder
            form={form}
            set={set}
            errors={errors}
            clear={clear}
            setErrors={setErrors}
            channels={channels}
            replyOptions={replyOptions}
            lookups={lookups}
            newAction={newAction}
            flowPreview={flowPreview}
          />
        </Suspense>
      ) : null}

      {view === 'json' ? (
        <div className="ne-field">
          <label className="ne-field__label" htmlFor={jsonId}>
            When / If / Then as JSON (triggers, conditions, actions)
          </label>
          <textarea
            id={jsonId}
            className={['ne-input', jsonError ? 'ne-input--error' : ''].filter(Boolean).join(' ')}
            rows={22}
            spellCheck={false}
            style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)' }}
            value={jsonText}
            aria-invalid={jsonError ? true : undefined}
            onChange={(e) => editJson(e.target.value)}
          />
          <FieldError message={jsonError ?? undefined} />
        </div>
      ) : null}

      {view === 'form' ? (
        <>
          <Card>
            <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
              {sectionTitle(
                1,
                'When…',
                'What should start this automation? You can pick more than one.',
              )}
              <WhenEditor form={form} set={set} clear={clear} errors={errors} channels={channels} />
            </div>
          </Card>

          {hasMessageTrigger ? (
            <Card>
              <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                {sectionTitle(
                  2,
                  'Only if… (optional)',
                  'Leave empty to run for every message. Add rules to react only to certain messages.',
                )}
                <IfEditor
                  form={form}
                  set={set}
                  clear={clear}
                  errors={errors}
                  replyOptions={replyOptions}
                />
              </div>
            </Card>
          ) : null}

          <Card>
            <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
              {sectionTitle(hasMessageTrigger ? 3 : 2, 'Then…', 'What should happen, in order.')}
              <FieldError message={errors.actions} />
              {form.actions.map((action, i) => (
                <ActionCard
                  key={i}
                  index={i}
                  count={form.actions.length}
                  action={action}
                  errors={errors}
                  clear={clear}
                  onChange={(next) =>
                    set({ actions: form.actions.map((a, j) => (j === i ? next : a)) })
                  }
                  onMove={(d) => {
                    const j = i + d;
                    if (j < 0 || j >= form.actions.length) return;
                    const next = [...form.actions];
                    [next[i], next[j]] = [next[j]!, next[i]!];
                    set({ actions: next });
                  }}
                  onRemove={() => set({ actions: form.actions.filter((_, j) => j !== i) })}
                  timeBased={timeBased}
                  lookups={lookups}
                />
              ))}
              {addingAction ? (
                <AddActionPicker
                  onPick={(kind) => {
                    set({ actions: [...form.actions, newAction(kind)] });
                    setAddingAction(false);
                    clear('actions');
                  }}
                  onCancel={() => setAddingAction(false)}
                />
              ) : (
                <div>
                  <Button type="button" variant="secondary" onClick={() => setAddingAction(true)}>
                    + Add another step
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}

/**
 * A read-only diagram of an automation (When -> Only if -> Then...), e.g. a
 * template's "What this template does". React Flow loads only when shown.
 */
export function AutomationDiagram({
  form,
  flows,
}: {
  form: AutomationFormState;
  flows: { key: string; name: string; description: string | null }[];
}) {
  const noop = () => undefined;
  return (
    <Suspense fallback={<PageState kind="loading" message="Loading the diagram…" />}>
      <AutomationBuilder
        readOnly
        form={form}
        set={noop}
        errors={{}}
        clear={noop}
        setErrors={noop}
        channels={[]}
        replyOptions={[]}
        lookups={{ templates: [], templatesLoading: false, flows, pipelines: [] }}
        newAction={(kind) => blankAction(kind)}
      />
    </Suspense>
  );
}
